import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  format, 
  parseISO, 
  startOfMonth, 
  endOfMonth, 
  isWithinInterval, 
  addDays, 
  isBefore, 
  startOfToday, 
  startOfWeek, 
  endOfWeek, 
  differenceInDays,
  isAfter,
  isSameDay
} from 'date-fns';
import { 
  fetchUserPreferences, 
  fetchMealPlans,
  generateMealPlan,
  regenerateMeal,
  clearError
} from '../../state/mealPlannerSlice';
import { setSelectedDate } from '../../state/calendarSlice';
import Topbar from '../global/TopBar';
import LoadingSpinner from '../../components/LoadingSpinner';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUtensils, FaCalendarAlt, FaUserCog, FaList, FaNotesMedical, FaTimes, FaRegCalendarAlt } from 'react-icons/fa';
import PreferenceForm from './components/PreferenceForm';
import MealCard from './components/MealCard';
import ShoppingList from './components/ShoppingList';
import MiniCalendar from './components/MiniCalendar';

export default function MealPlanner({ onNotificationAction }) {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const { userPreferences, mealPlans, status, error } = useSelector(state => state.mealPlanner);
  const selectedDate = useSelector(state => new Date(state.calendar.selectedDate));
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'preferences', 'shopping'
  const [showPreferencesForm, setShowPreferencesForm] = useState(false);
  const [activeMeal, setActiveMeal] = useState(null);
  const [showMealDetails, setShowMealDetails] = useState(false);
  const [temporaryDate, setTemporaryDate] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateNotes, setGenerateNotes] = useState('');
  const [generatePeriod, setGeneratePeriod] = useState('day'); // 'day' or 'week'
  const [successMessage, setSuccessMessage] = useState('');
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState(selectedDate);
  const [dateRangeEnd, setDateRangeEnd] = useState(selectedDate);
  const [hasCustomDateRange, setHasCustomDateRange] = useState(false); // Track if user has explicitly set a date range
  const [dateRangeError, setDateRangeError] = useState(''); // Track date range errors
  const [errorMessage, setErrorMessage] = useState('');
  const MAX_RANGE_DAYS = 4; // Maximum allowed days
  
  // Refs to prevent infinite loops and track request state
  const fetchedMonthsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true);
  
  // Format date for API calls
  const formattedDate = format(selectedDate, 'yyyy-MM-dd');
  
  // For testing: If there are no meal plans on the selected date, check if we have any meal plans at all
  // This is just for demonstration purposes with the hardcoded "2025-04-24" data
  useEffect(() => {
    // If no meals found for selected date but we have meal plans for other dates
    const mealsOnSelectedDate = mealPlans[formattedDate] || {};
    
    if (Object.keys(mealPlans).length > 0 && Object.keys(mealsOnSelectedDate).length === 0) {
      // Get the first available date that has meal plans
      const availableDates = Object.keys(mealPlans);
      if (availableDates.length > 0) {
        setTemporaryDate(availableDates[0]);
        console.log('No meals for selected date. Available date:', availableDates[0]);
      }
    }
  }, [mealPlans, formattedDate]);
  
  // Get meals for the selected date or use temporary date for testing
  const mealsForSelectedDate = temporaryDate ? (mealPlans[temporaryDate] || {}) : (mealPlans[formattedDate] || {});
  
  // Get all meals in the selected date range
  const getMealsForDateRange = () => {
    // For single-date view
    if (dateRangeStart.getTime() === dateRangeEnd.getTime()) {
      // If using a temporary date for demo purposes
      if (temporaryDate) {
        return { [temporaryDate]: mealPlans[temporaryDate] || {} };
      }
      
      // Use the most current selected date
      const currentDateKey = format(selectedDate, 'yyyy-MM-dd');
      return { [currentDateKey]: mealPlans[currentDateKey] || {} };
    }
    
    // For date ranges, collect all meals within the range
    const mealsInRange = {};
    const currentDate = new Date(dateRangeStart);
    const endDate = new Date(dateRangeEnd);
    
    // Iterate through each date in the range
    while (currentDate <= endDate) {
      const dateKey = format(currentDate, 'yyyy-MM-dd');
      if (mealPlans[dateKey]) {
        mealsInRange[dateKey] = mealPlans[dateKey];
      } else {
        mealsInRange[dateKey] = {}; // Empty placeholder for dates without meals
      }
      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return mealsInRange;
  };
  
  // Get meals based on whether we're viewing a date range or single date
  const mealsToDisplay = getMealsForDateRange();
  
  // Debug: Log values to understand what's happening
  useEffect(() => {
    console.log('Selected date (formatted):', formattedDate);
    console.log('Temporary date for testing:', temporaryDate);
    console.log('Available meal dates:', Object.keys(mealPlans));
    console.log('Meals for selected date:', mealsForSelectedDate);
    console.log('All meal plans:', mealPlans);
  }, [formattedDate, temporaryDate, mealPlans, mealsForSelectedDate]);
  
  // Determine if preferences need to be set
  const needsPreferences = !userPreferences || 
    !userPreferences.dietaryRestrictions || 
    !userPreferences.healthGoals;
  
  // Effect to load user preferences when component mounts
  useEffect(() => {
    if (user?.id && status === 'idle') {
      dispatch(fetchUserPreferences(user.id));
    }
    
    // Clear errors when component unmounts
    return () => {
      dispatch(clearError());
    };
  }, [dispatch, user?.id, status]);
  
  // Effect to load meal plans for the current month - revised to prevent loops
  useEffect(() => {
    // Skip if no user, no preferences, or already loading
    if (!user?.id || !userPreferences?.dietaryRestrictions || status === 'loading') {
      return;
    }
    
    const monthKey = format(selectedDate, 'yyyy-MM');
    const monthStart = format(startOfMonth(selectedDate), 'yyyy-MM-dd');
    const monthEnd = format(endOfMonth(selectedDate), 'yyyy-MM-dd');
    
    // Check if we've already fetched this month
    if (fetchedMonthsRef.current.has(monthKey)) {
      return;
    }
    
    // Mark that we've fetched this month to prevent duplicate requests
    fetchedMonthsRef.current.add(monthKey);
    
    // Dispatch fetch action
    dispatch(fetchMealPlans({ 
      userId: user.id, 
      startDate: monthStart, 
      endDate: monthEnd 
    }));
    
  }, [dispatch, user?.id, selectedDate, userPreferences?.dietaryRestrictions, status]);
  
  // Effect to handle date changes and ensure mealsToDisplay is updated
  useEffect(() => {
    // When selected date changes, update the date range to match if in single-day mode
    // But ONLY if we don't have a custom date range set by the user
    // AND we're not explicitly showing a past date range
    if (dateRangeStart.getTime() === dateRangeEnd.getTime() && !temporaryDate && !hasCustomDateRange) {
      // Only set state if values actually changed to prevent infinite loop
      if (dateRangeStart.getTime() !== selectedDate.getTime() || 
          dateRangeEnd.getTime() !== selectedDate.getTime()) {
        setDateRangeStart(selectedDate);
        setDateRangeEnd(selectedDate);
      }
    }
    
    // Clear any temporary date when the selected date changes
    if (temporaryDate && selectedDate) {
      setTemporaryDate('');
    }
  }, [selectedDate, hasCustomDateRange, temporaryDate, dateRangeStart, dateRangeEnd]);
  
  // Handle view mode changes
  const handleViewChange = (mode) => {
    setViewMode(mode);
    if (mode === 'preferences') {
      setShowPreferencesForm(true);
    }
  };
  
  // Handle generating meal plan
  const handleGenerateMealPlan = () => {
    // Get today's date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Use today's date or selected date, whichever is later
    let initialDate = selectedDate;
    if (isBefore(initialDate, today)) {
      initialDate = today;
    }
    
    setDateRangeStart(initialDate);
    setDateRangeEnd(initialDate);
    setGeneratePeriod('day');
    setDateRangeError(''); // Clear any previous errors
    setShowGenerateModal(true);
  };
  
  // Handle date range selection
  const handleDateRangeSelect = (startDate, endDate, context = 'view') => {
    setDateRangeStart(startDate);
    setDateRangeEnd(endDate);
    setShowDateRangePicker(false);
    
    // Calculate date difference in days
    const daysDiff = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    // Only validate the date range for the 'generate' context
    if (context === 'generate') {
      // Validate date range length
      if (daysDiff > MAX_RANGE_DAYS - 1) {
        setDateRangeError(`Date range cannot exceed ${MAX_RANGE_DAYS} days. Please select a shorter range.`);
      } else {
        setDateRangeError('');
      }
      
      // Check for past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (isBefore(startDate, today)) {
        setDateRangeError('Cannot generate meal plans for past dates. Please select future dates.');
      }
    } else {
      // In viewing context, no restrictions on date ranges
      setDateRangeError('');
    }
    
    // Set flag for custom date range
    setHasCustomDateRange(daysDiff > 0);
    
    // Update generate period based on selection
    if (daysDiff === 0) {
      setGeneratePeriod('day');
      
      // If it's a single day, update the calendar's selected date
      dispatch(setSelectedDate({ selectedDate: startDate.toString() }));
      
      // Clear temporary date if set
      if (temporaryDate) {
        setTemporaryDate('');
      }
    } else if (daysDiff === MAX_RANGE_DAYS - 1) {
      setGeneratePeriod('week');
      // DON'T update selectedDate for date ranges
    } else {
      setGeneratePeriod('custom');
      // DON'T update selectedDate for date ranges
    }
    
    // Fetch meal plans for the selected date range
    if (user?.id) {
      dispatch(fetchMealPlans({
        userId: user.id,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      }));
    }
  };
  
  // Handle canceling the meal plan generation
  const handleCancelGenerate = () => {
    setShowGenerateModal(false);
    setGenerateNotes('');
    setShowDateRangePicker(false);
    setDateRangeError(''); // Clear any errors
  };
  
  // Handle submitting the meal plan generation with notes
  const handleSubmitGenerate = () => {
    if (user?.id) {
      // Get today's date for comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      // Format dates for API
      const startDate = format(dateRangeStart, 'yyyy-MM-dd');
      let endDate = format(dateRangeEnd, 'yyyy-MM-dd');
      
      // If using predefined periods
      if (generatePeriod === 'day') {
        endDate = startDate;
      } else if (generatePeriod === 'week') {
        // Add MAX_RANGE_DAYS - 1 days to the start date 
        const endDateObj = new Date(dateRangeStart);
        endDateObj.setDate(dateRangeStart.getDate() + MAX_RANGE_DAYS - 1);
        endDate = format(endDateObj, 'yyyy-MM-dd');
        setDateRangeEnd(endDateObj);
      }
      
      // Prevent generating meal plans for past dates
      if (isBefore(dateRangeStart, today)) {
        setDateRangeError(`Cannot generate meal plans for past dates. Please select future dates.`);
        return;
      }
      
      // Calculate days difference to validate
      const daysDiff = Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
      
      // Validate date range one more time as a safety check
      if (daysDiff > MAX_RANGE_DAYS - 1) {
        setDateRangeError(`Date range cannot exceed ${MAX_RANGE_DAYS} days. Please select a shorter range.`);
        return;
      }
      
      // Clear any previous errors since we passed validation
      setDateRangeError('');
      
      // Prepare the base payload
      const payload = {
        userId: user.id,
        startDate: startDate,
        endDate: endDate,
        notes: generateNotes // User's notes
      };
      
      // Check if we have missing meals data in session storage
      const missingMealsDataStr = sessionStorage.getItem('missingMealsData');
      
      if (missingMealsDataStr) {
        try {
          // Parse the JSON data from session storage
          const missingMealsData = JSON.parse(missingMealsDataStr);
          
          // Add the missing meals information directly to the payload
          // This ensures it gets sent to the API
          payload.missingMeals = missingMealsData;
          payload.fillMissingOnly = true;
          
          // Format missing dates for logging
          const formattedDates = missingMealsData.map(item => 
            `${item.date} (${item.missingMealTypes.join(', ')})`
          ).join(', ');
          
          console.log(`Filling missing meals for dates: ${formattedDates}`);
          
          // Clear the session storage
          sessionStorage.removeItem('missingMealsData');
        } catch (error) {
          console.error('Error parsing missing meals data:', error);
        }
      }
      
      dispatch(generateMealPlan(payload))
        .unwrap()
        .then(() => {
          // Set success message - only runs when the API call succeeds
          let message;
          if (payload.fillMissingOnly) {
            message = `Successfully filled in missing meals from ${format(dateRangeStart, 'MMMM d')} to ${format(dateRangeEnd, 'MMMM d')}!`;
          } else if (daysDiff === 0) {
            message = `Successfully generated meal plan for ${format(dateRangeStart, 'MMMM d')}!`;
          } else if (daysDiff === MAX_RANGE_DAYS - 1) {
            message = `Successfully generated meal plans for ${daysDiff + 1} days starting ${format(dateRangeStart, 'MMMM d')}!`;
          } else {
            message = `Successfully generated meal plans from ${format(dateRangeStart, 'MMMM d')} to ${format(dateRangeEnd, 'MMMM d')}!`;
          }
          
          setSuccessMessage(message);
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setSuccessMessage('');
          }, 5000);
        })
        .catch((error) => {
          // Handle error case explicitly
          setSuccessMessage(''); // Clear any success message
          const errorMsg = error?.message || 'Failed to generate meal plan. Please try again.';
          setErrorMessage(errorMsg);
          
          // Clear error message after 5 seconds
          setTimeout(() => {
            setErrorMessage('');
          }, 5000);
          
          // Display error using the existing error state from Redux
          console.error('Meal plan generation failed:', errorMsg);
        });
      
      setShowGenerateModal(false);
      setGenerateNotes('');
      setDateRangeError('');
    }
  };
  
  // Handle regenerating a specific meal
  const handleRegenerateMeal = (meal, notes) => {
    // Check if this is generating a new meal (not a regeneration)
    const isNewMeal = !meal.recipe;
    
    console.log('Handling meal regeneration/generation:', { meal, notes, isNewMeal });
    
    // Get the appropriate date and meal type
    const date = isNewMeal ? meal.date : meal.date;
    const mealType = isNewMeal ? meal.mealType : meal.mealType;
    
    if (user && date && mealType) {
      console.log(`${isNewMeal ? 'Generating' : 'Regenerating'} ${mealType} for ${date}`);
      dispatch(regenerateMeal({
        userId: user.id,
        date,
        mealType,
        notes
      }))
        .unwrap()
        .then(() => {
          // Set success message
          const message = `Successfully ${isNewMeal ? 'generated' : 'regenerated'} ${mealType} for ${format(new Date(date), 'MMMM d')}!`;
          setSuccessMessage(message);
          setTimeout(() => {
            setSuccessMessage('');
          }, 5000);
        })
        .catch((error) => {
          // Handle error case
          setSuccessMessage(''); // Clear any success message
          const errorMsg = error?.message || 'Failed to regenerate meal. Please try again.';
          setErrorMessage(errorMsg);
          
          // Clear error message after 5 seconds
          setTimeout(() => {
            setErrorMessage('');
          }, 5000);
          
          console.error('Meal regeneration failed:', errorMsg);
        });
    }
  };
  
  // Handle viewing meal details
  const handleViewMealDetails = (meal) => {
    setActiveMeal(meal);
    setShowMealDetails(true);
  };
  
  // Handle preferences form completion
  const handlePreferencesComplete = () => {
    setViewMode('calendar');
  };
  
  // Handle print recipe
  const handlePrintRecipe = (recipe) => {
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>${recipe.name} Recipe</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              line-height: 1.6;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            h1 { color: #d64a84; }
            h2 { 
              color: #444;
              border-bottom: 1px solid #eee;
              padding-bottom: 10px;
              margin-top: 30px;
            }
            .description { 
              font-style: italic;
              margin-bottom: 25px;
            }
            .meta {
              display: flex;
              justify-content: space-between;
              background: #f5f5f5;
              padding: 15px;
              border-radius: 8px;
              margin-bottom: 25px;
            }
            .meta-item {
              text-align: center;
            }
            .meta-value {
              font-weight: bold;
              font-size: 18px;
            }
            .meta-label {
              font-size: 12px;
              color: #666;
            }
            ul, ol {
              padding-left: 25px;
            }
            li {
              margin-bottom: 10px;
            }
            .nutrients {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin-bottom: 30px;
            }
            .nutrient {
              text-align: center;
              padding: 10px;
              background: #f9f9f9;
              border-radius: 8px;
            }
            .print-footer {
              margin-top: 40px;
              font-size: 12px;
              color: #999;
              text-align: center;
              border-top: 1px solid #eee;
              padding-top: 20px;
            }
            @media print {
              body { font-size: 12pt; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${recipe.name}</h1>
          <p class="description">${recipe.description}</p>
          
          <div class="meta">
            <div class="meta-item">
              <div class="meta-value">${recipe.cuisineType}</div>
              <div class="meta-label">Cuisine</div>
            </div>
            <div class="meta-item">
              <div class="meta-value">${recipe.difficulty}</div>
              <div class="meta-label">Difficulty</div>
            </div>
            <div class="meta-item">
              <div class="meta-value">${recipe.prepTime} min</div>
              <div class="meta-label">Prep Time</div>
            </div>
            <div class="meta-item">
              <div class="meta-value">${recipe.cookTime} min</div>
              <div class="meta-label">Cook Time</div>
            </div>
            <div class="meta-item">
              <div class="meta-value">${recipe.prepTime + recipe.cookTime} min</div>
              <div class="meta-label">Total Time</div>
            </div>
          </div>
          
          <h2>Nutritional Information</h2>
          <div class="nutrients">
            <div class="nutrient">
              <div class="meta-value">${recipe.nutrients.calories}</div>
              <div class="meta-label">Calories</div>
            </div>
            <div class="nutrient">
              <div class="meta-value">${recipe.nutrients.protein}g</div>
              <div class="meta-label">Protein</div>
            </div>
            <div class="nutrient">
              <div class="meta-value">${recipe.nutrients.carbs}g</div>
              <div class="meta-label">Carbs</div>
            </div>
            <div class="nutrient">
              <div class="meta-value">${recipe.nutrients.fat}g</div>
              <div class="meta-label">Fat</div>
            </div>
          </div>
          
          <h2>Ingredients</h2>
          <ul>
            ${recipe.ingredients.map(ingredient => 
              `<li>${ingredient.quantity} ${ingredient.unit} ${ingredient.name}</li>`
            ).join('')}
          </ul>
          
          <h2>Instructions</h2>
          <ol>
            ${recipe.instructions.map(step => 
              `<li>${step}</li>`
            ).join('')}
          </ol>
          
          <div class="print-footer">
            Recipe from Jingleez Meal Planner
          </div>
          
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-pri-dark via-[#1a1c2e] to-sec-dark text-pri-white">
      <Topbar />
      
      {/* Hero Section - Match design of Connections page */}
      <div className="relative bg-gradient-to-b from-pri-dark/90 via-sec-dark/90 to-pri-dark/90 shadow-2xl">
        {/* Glossy Overlay with soft-pink accents */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-mint-green/5" />
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
          <div className="absolute -left-24 -top-24 w-48 h-48 bg-soft-pink/20 rounded-full filter blur-[80px] opacity-30" />
          <div className="absolute -right-24 -bottom-24 w-48 h-48 bg-mint-green/20 rounded-full filter blur-[80px] opacity-20" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl font-bold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent"
            >
              Meal Planner
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-pri-gray mb-6"
            >
              Plan your meals, generate recipes, and create shopping lists
            </motion.p>
          </div>
          <div className="flex justify-center mb-2">
            <div className="flex bg-pri-dark/50 p-1 rounded-xl overflow-hidden border border-gray-700/50 shadow-lg w-full sm:w-auto">
              <button
                onClick={() => handleViewChange('calendar')}
                className={`flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-300 text-xs sm:text-sm flex-1 sm:flex-auto border-2 border-soft-pink/20 ${
                  viewMode === 'calendar' 
                    ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-md' 
                    : 'text-pri-white/70 hover:text-pri-white'
                }`}
              >
                <FaCalendarAlt className="text-xs sm:text-base" />
                <span className="sm:ml-1">Calendar</span>
              </button>
              <button
                onClick={() => handleViewChange('shopping')}
                className={`flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-300 text-xs sm:text-sm flex-1 sm:flex-auto border-2 border-soft-pink/20 ${
                  viewMode === 'shopping' 
                    ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-md' 
                    : 'text-pri-white/70 hover:text-pri-white'
                }`}
              >
                <FaList className="text-xs sm:text-base" />
                <span className="sm:ml-1">Shopping List</span>
              </button>
              <button
                onClick={() => handleViewChange('preferences')}
                className={`flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-300 text-xs sm:text-sm flex-1 sm:flex-auto border-2 border-soft-pink/20 ${
                  viewMode === 'preferences' 
                    ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-md' 
                    : 'text-pri-white/70 hover:text-pri-white'
                }`}
              >
                <FaUserCog className="text-xs sm:text-base" />
                <span className="sm:ml-1">Preferences</span>
              </button>
            </div>
          </div>
        </div>        
      </div>
      
      {/* Rest of content */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* View Toggle */}
        
        
        {/* Success Message */}
        <AnimatePresence>
          {successMessage && (
            <motion.div 
              className="bg-green-900/30 border border-green-500/30 text-green-300 px-4 py-3 rounded relative mb-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <strong className="font-bold">Success! </strong>
              <span className="block sm:inline">{successMessage}</span>
              {successMessage.includes('week') && (
                <p className="text-sm mt-1">
                  Browse through the days of the week in the calendar to see all your new meal plans.
                </p>
              )}
              <button 
                className="absolute top-0 bottom-0 right-0 px-4"
                onClick={() => setSuccessMessage('')}
              >
                <FaTimes className="text-green-300 hover:text-green-200" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Error Message */}
        <AnimatePresence>
          {errorMessage && (
            <motion.div 
              className="bg-red-900/30 border border-red-500/30 text-red-300 px-4 py-3 rounded relative mb-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <strong className="font-bold">Error! </strong>
              <span className="block sm:inline">{errorMessage}</span>
              <button 
                className="absolute top-0 bottom-0 right-0 px-4"
                onClick={() => setErrorMessage('')}
              >
                <FaTimes className="text-red-300 hover:text-red-200" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Loading State */}
        {status === 'loading' && (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        )}
        
        {/* Error State */}
        {status === 'failed' && error && (
          <div className="bg-red-900/30 border border-red-500/30 text-red-300 px-4 py-3 rounded relative mb-4">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        

        
        {/* Main Content Area */}
        <div className="bg-gradient-to-br from-pri-dark/90 to-sec-dark/95 rounded-2xl shadow-lg p-6 border border-white/5">
          {viewMode === 'calendar' && (
            <div>
              {/* Needs Preferences Message - Moved to top of calendar view */}
              {needsPreferences && status !== 'loading' && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-soft-pink/10 to-mint-green/10 border border-white/10 text-white p-4 rounded-lg mb-6"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-1">Set Up Your Preferences</h3>
                      <p className="text-sm text-white/70">To get personalized meal plans, please set your dietary preferences and health goals.</p>
                    </div>
                    <button
                      onClick={() => handleViewChange('preferences')}
                      className="px-4 py-2 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark rounded-lg text-sm font-medium hover:shadow-md transition-all whitespace-nowrap"
                    >
                      Set Preferences
                    </button>
                  </div>
                </motion.div>
              )}

              <h2 className="text-xl font-semibold mb-4 flex items-center justify-between">
                <div className="flex items-center">
                  <FaUtensils className="mr-2 text-soft-pink" />
                  <span className="bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">{temporaryDate ? format(new Date(temporaryDate), 'MMMM d, yyyy') : format(selectedDate, 'MMMM d, yyyy')}</span>
                </div>
                
                {/* Date Range Selector */}
                <div className="relative">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      // Initialize with the current selected date
                      setDateRangeStart(selectedDate);
                      setDateRangeEnd(selectedDate);
                      setShowDateRangePicker(true);
                    }}
                    className="flex items-center gap-2 bg-pri-dark/40 hover:bg-pri-dark/60 px-3 py-1.5 rounded-lg border border-white/10 text-sm bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark"
                  >
                    <FaRegCalendarAlt className="text-pri-dark" />
                    <span>Select Date Range</span>
                  </motion.button>
                  
                  {/* MiniCalendar Popover */}
                  <AnimatePresence>
                    {showDateRangePicker && (
                      <motion.div 
                        className="absolute top-full right-0 mt-2 z-50"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <MiniCalendar 
                          onSelectRange={(start, end) => {
                            handleDateRangeSelect(start, end, 'view');
                          }}
                          onClose={() => setShowDateRangePicker(false)}
                          initialStartDate={dateRangeStart}
                          initialEndDate={dateRangeEnd}
                          preventPastDates={false}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </h2>
              
              {/* Date Range Indicator */}
              {dateRangeStart && dateRangeEnd && dateRangeStart.getTime() !== dateRangeEnd.getTime() && (
                <div className="mb-4 bg-soft-pink/10 border border-soft-pink/30 text-soft-pink px-4 py-3 rounded-lg">
                  <p className="text-sm flex items-center">
                    <FaCalendarAlt className="mr-2" />
                    <span>
                      Viewing meal plans from <strong>{format(dateRangeStart, 'MMMM d, yyyy')}</strong> to <strong>{format(dateRangeEnd, 'MMMM d, yyyy')}</strong>
                      ({Math.round((dateRangeEnd - dateRangeStart) / (1000 * 60 * 60 * 24) + 1)} days)
                    </span>
                  </p>
                  
                  {/* Past Date Notice - only displayed when viewing (not generating) */}
                  {(() => {
                    // Check if dateRangeStart is in the past
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    if (isBefore(dateRangeStart, today)) {
                      return (
                        <div className="mt-2 bg-blue-900/30 border border-blue-500/30 p-2 rounded text-blue-300 text-sm">
                          <p className="flex items-start">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>You are viewing historical meal plans. To generate new plans, please select future dates.</span>
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {dateRangeError && (
                    <div className="mt-2 bg-red-900/30 border border-red-500/30 p-2 rounded text-red-300 text-sm">
                      <p className="flex items-start">
                        <FaTimes className="mr-1 mt-0.5 flex-shrink-0" />
                        <span>{dateRangeError}</span>
                      </p>
                    </div>
                  )}
                  
                  <div className="flex gap-2 mt-2">
                    <button 
                      onClick={() => {
                        // Get today's date
                        const today = new Date();
                        
                        // Update calendar's selected date
                        dispatch(setSelectedDate({ selectedDate: today.toString() }));
                        
                        // Reset to today
                        setDateRangeStart(today);
                        setDateRangeEnd(today); 
                        setGeneratePeriod('day');
                        setHasCustomDateRange(false); // Clear custom date range flag
                        setDateRangeError(''); // Clear any errors
                        
                        // Clear temporaryDate if set
                        if (temporaryDate) {
                          setTemporaryDate('');
                        }
                      }}
                      className="text-xs bg-soft-pink/20 hover:bg-soft-pink/30 px-2 py-1 rounded"
                    >
                      Reset to Today
                    </button>
                    <button
                      onClick={() => {
                        // Calculate days difference
                        const daysDiff = Math.round((dateRangeEnd - dateRangeStart) / (1000 * 60 * 60 * 24));
                        
                        // Validate date range
                        if (daysDiff > MAX_RANGE_DAYS - 1) {
                          setDateRangeError(`Date range cannot exceed ${MAX_RANGE_DAYS} days. Please select a shorter range.`);
                          // Show an error notification instead of opening the modal
                          return;
                        }
                        
                        // Clear any existing errors
                        setDateRangeError('');
                        
                        // Open generate modal with current date range
                        setShowGenerateModal(true);
                      }}
                      disabled={needsPreferences || isBefore(dateRangeStart, startOfToday()) || Object.values(mealsToDisplay).some(dateMeals => Object.keys(dateMeals).length > 0)}
                      className={`text-xs ${
                        needsPreferences
                          ? "bg-gray-500/50 cursor-not-allowed text-white/50"
                          : isBefore(dateRangeStart, startOfToday()) || Object.values(mealsToDisplay).some(dateMeals => Object.keys(dateMeals).length > 0)
                            ? "bg-gray-500 cursor-not-allowed"
                            : "bg-soft-pink/20 hover:bg-soft-pink/30"
                      } px-2 py-1 rounded flex items-center relative group`}
                      title={
                        needsPreferences
                          ? "Set preferences to generate meal plans"
                          : isBefore(dateRangeStart, startOfToday()) 
                            ? "Cannot generate meal plans for past dates" 
                            : Object.values(mealsToDisplay).some(dateMeals => Object.keys(dateMeals).length > 0)
                              ? "Can only generate for dates with no existing meals"
                              : ""
                      }
                    >
                      <FaUtensils className="mr-1 text-[10px]" />
                      {needsPreferences ? 'Set Preferences First' : 'Generate for Range'}
                    </button>
                  </div>
                </div>
              )}
              
              {temporaryDate && (
                <div className="mb-4 bg-blue-900/30 border border-blue-500/30 text-blue-300 px-4 py-3 rounded relative">
                  <p className="text-sm">
                    <strong>Note:</strong> Showing test data for {format(new Date(temporaryDate), 'MMMM d, yyyy')} since no meal plans exist for your selected date.
                  </p>
                  <button 
                    className="text-xs underline ml-2 text-blue-200 hover:text-blue-100"
                    onClick={() => setTemporaryDate('')}
                  >
                    Return to selected date
                  </button>
                </div>
              )}
              
              {Object.keys(mealsToDisplay).length === 0 ? (
                <div className="text-center py-10 mb-8 bg-pri-dark/20 rounded-lg border border-pri-dark/40">
                  <p className="text-pri-white/70 mb-5">No meals planned for {format(dateRangeStart, 'MMMM d, yyyy')}.</p>
                  {!isBefore(dateRangeStart, startOfToday()) ? (
                    <motion.button
                      onClick={() => {
                        // Call handleGenerateMealPlan to generate all meals for this date
                        handleGenerateMealPlan();
                      }}
                      disabled={needsPreferences}
                      className={`px-6 py-3 text-sm rounded-lg flex items-center mx-auto font-medium transition-all ${
                        needsPreferences 
                          ? 'bg-gray-500/50 cursor-not-allowed text-white/50'
                          : 'bg-soft-pink text-pri-dark hover:bg-soft-pink/90'
                      }`}
                      whileHover={!needsPreferences ? { scale: 1.05 } : {}}
                      whileTap={!needsPreferences ? { scale: 0.95 } : {}}
                    >
                      <FaUtensils className="mr-2" />
                      {needsPreferences ? 'Set Preferences to Generate' : 'Generate Meals'}
                    </motion.button>
                  ) : (
                    <p className="text-xs text-yellow-400 mt-2">Cannot generate meal plans for past dates.</p>
                  )}
                </div>
              ) : (
                // Check if we're viewing a date range or just a single date
                dateRangeStart.getTime() === dateRangeEnd.getTime() ? (
                  // Single date view - show the standard 3-column layout
                  <div>
                    {/* Check if any meals exist for this date */}
                    {Object.values(Object.values(mealsToDisplay)[0] || {}).length === 0 ? (
                      // No meals - show a button to generate all
                      <div className="text-center py-10 mb-8 bg-pri-dark/20 rounded-lg border border-pri-dark/40">
                        <p className="text-pri-white/70 mb-5">No meals planned for {format(dateRangeStart, 'MMMM d, yyyy')}.</p>
                        {!isBefore(dateRangeStart, startOfToday()) ? (
                          <motion.button
                            onClick={() => {
                              // Call handleGenerateMealPlan to generate all meals for this date
                              handleGenerateMealPlan();
                            }}
                            disabled={needsPreferences}
                            className={`px-6 py-3 text-sm rounded-lg flex items-center mx-auto font-medium transition-all ${
                              needsPreferences 
                                ? 'bg-gray-500/50 cursor-not-allowed text-white/50'
                                : 'bg-soft-pink text-pri-dark hover:bg-soft-pink/90'
                            }`}
                            whileHover={!needsPreferences ? { scale: 1.05 } : {}}
                            whileTap={!needsPreferences ? { scale: 0.95 } : {}}
                          >
                            <FaUtensils className="mr-2" />
                            {needsPreferences ? 'Set Preferences to Generate' : 'Generate Meals'}
                          </motion.button>
                        ) : (
                          <p className="text-xs text-yellow-400 mt-2">Cannot generate meal plans for past dates.</p>
                        )}
                      </div>
                    ) : null}
                    
                    {/* Meal cards container */}
                    <div className="relative">
                      <div className="flex overflow-x-auto whitespace-nowrap pb-4 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-3 md:gap-6 md:whitespace-normal">
                        {['breakfast', 'lunch', 'dinner'].map(mealType => {
                          // Check if the current date is in the past
                          const isPastDate = isBefore(dateRangeStart, startOfToday());
                          
                          return (
                            <div key={mealType} className="inline-block w-[250px] h-[280px] mx-2 whitespace-normal align-top first:ml-4 last:mr-4 md:w-auto md:h-auto md:mx-0 md:first:ml-0 md:last:mr-0 md:block">
                              <MealCard
                                meal={Object.values(mealsToDisplay)[0][mealType]}
                                mealType={mealType}
                                onViewDetails={handleViewMealDetails}
                                onRegenerate={handleRegenerateMeal}
                                canGenerateMeal={false} // Disable individual generate buttons
                                date={format(dateRangeStart, 'yyyy-MM-dd')}
                              />
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Indicators for horizontal scroll */}
                      <div className="md:hidden">
                        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-pri-dark/80 to-transparent w-10 h-full pointer-events-none z-10"></div>
                        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-l from-pri-dark/80 to-transparent w-10 h-full pointer-events-none z-10"></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Multi-date view - show each date with its meals
                  <div className="space-y-8">
                    {/* Date range controls */}
                    <div className="bg-pri-dark/30 rounded-lg p-4 border border-white/10 mb-6">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                          <h3 className="text-sm font-medium text-pri-white/70 mb-1">Date Range</h3>
                          <p className="text-soft-pink font-medium">
                            {format(dateRangeStart, 'MMM d')} - {format(dateRangeEnd, 'MMM d, yyyy')}
                            <span className="ml-2 text-xs bg-soft-pink/20 px-2 py-0.5 rounded-full">
                              {Math.round((dateRangeEnd - dateRangeStart) / (1000 * 60 * 60 * 24) + 1)} days
                            </span>
                          </p>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap">
                          <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => setShowDateRangePicker(true)}
                            className="text-sm bg-pri-dark/60 hover:bg-pri-dark/80 px-3 py-1.5 rounded-lg flex items-center border border-white/10"
                          >
                            <FaRegCalendarAlt className="mr-1.5 text-soft-pink" />
                            Change Range
                          </motion.button>
                          
                          <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => {
                              // Calculate days difference
                              const daysDiff = Math.round((dateRangeEnd - dateRangeStart) / (1000 * 60 * 60 * 24));
                              
                              // Validate date range
                              if (daysDiff > MAX_RANGE_DAYS - 1) {
                                setDateRangeError(`Date range cannot exceed ${MAX_RANGE_DAYS} days. Please select a shorter range.`);
                                // Show an error notification instead of opening the modal
                                return;
                              }
                              
                              // Clear any existing errors
                              setDateRangeError('');
                              
                              // Open generate modal with current date range
                              setShowGenerateModal(true);
                            }}
                            disabled={needsPreferences || isBefore(dateRangeStart, startOfToday()) || Object.values(mealsToDisplay).some(dateMeals => Object.keys(dateMeals).length > 0)}
                            className={`text-sm ${
                              needsPreferences
                                ? "bg-gray-500/50 cursor-not-allowed text-white/50"
                                : isBefore(dateRangeStart, startOfToday()) || Object.values(mealsToDisplay).some(dateMeals => Object.keys(dateMeals).length > 0)
                                  ? "bg-gray-500 cursor-not-allowed"
                                  : "bg-soft-pink hover:bg-soft-pink/90"
                            } text-pri-dark px-3 py-1.5 rounded-lg flex items-center relative group`}
                            title={
                              needsPreferences
                                ? "Set preferences to generate meal plans"
                                : isBefore(dateRangeStart, startOfToday()) 
                                  ? "Cannot generate meal plans for past dates" 
                                  : Object.values(mealsToDisplay).some(dateMeals => Object.keys(dateMeals).length > 0)
                                    ? "Can only generate for dates with no existing meals"
                                    : ""
                            }
                          >
                            <FaUtensils className="mr-1.5" />
                            Generate for Range
                            {/* Tooltip */}
                            {Object.values(mealsToDisplay).some(dateMeals => Object.keys(dateMeals).length > 0) && !isBefore(dateRangeStart, startOfToday()) && (
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                Can only generate for dates with no existing meals
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                              </div>
                            )}
                          </motion.button>
                        </div>
                      </div>
                      
                      {/* Overview Stats */}
                      {(() => {
                        // Calculate how many meal slots are filled vs. total
                        const totalDays = Math.round((dateRangeEnd - dateRangeStart) / (1000 * 60 * 60 * 24)) + 1;
                        const totalSlots = totalDays * 3; // 3 meals per day
                        let filledSlots = 0;
                        
                        // Count filled slots
                        Object.values(mealsToDisplay).forEach(dateMeals => {
                          filledSlots += Object.keys(dateMeals).length;
                        });
                        
                        // Calculate percentage
                        const fillPercentage = Math.round((filledSlots / totalSlots) * 100);
                        
                        return (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                              <p className="text-xs text-pri-white/70">
                                <span className="text-soft-pink font-medium text-sm">{filledSlots}</span> of <span className="font-medium">{totalSlots}</span> meal slots filled 
                                <span className="ml-2 text-xs">({fillPercentage}%)</span>
                              </p>
                              
                              <div className="flex gap-2 items-center">
                                <button 
                                  className="text-xs bg-pri-dark/50 hover:bg-pri-dark/70 px-2 py-1 rounded text-pri-white/80 hover:text-pri-white transition-colors"
                                  onClick={() => {
                                    // Scroll to first date with missing meals
                                    const firstEmptyDateElement = document.querySelector('[data-needs-meals="true"]');
                                    if (firstEmptyDateElement) {
                                      firstEmptyDateElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                  }}
                                >
                                  Jump to First Empty Day
                                </button>
                                
                                {fillPercentage < 100 && (
                                  <button 
                                    className={`text-xs ${
                                      isBefore(dateRangeStart, startOfToday())
                                        ? "bg-gray-500 text-gray-300 cursor-not-allowed"
                                        : "bg-soft-pink/20 hover:bg-soft-pink/30 text-soft-pink"
                                    } px-2 py-1 rounded flex items-center`}
                                    onClick={() => {
                                      // Identify dates with missing meals
                                      const datesWithMissingMeals = [];
                                      const currentDate = new Date(dateRangeStart);
                                      const endDate = new Date(dateRangeEnd);
                                      
                                      while (currentDate <= endDate) {
                                        const dateKey = format(currentDate, 'yyyy-MM-dd');
                                        // Check if the date has any meals
                                        const dateMeals = mealsToDisplay[dateKey] || {};
                                        const mealCount = Object.keys(dateMeals).length;
                                        
                                        // If there are no meals or not all meal types (breakfast, lunch, dinner)
                                        if (mealCount < 3) {
                                          // Don't include past dates
                                          if (!isBefore(currentDate, startOfToday())) {
                                            datesWithMissingMeals.push({
                                              date: new Date(currentDate),
                                              dateKey: dateKey,
                                              existingMeals: Object.keys(dateMeals)
                                            });
                                          }
                                        }
                                        
                                        // Move to next day
                                        currentDate.setDate(currentDate.getDate() + 1);
                                      }

                                      // If there are dates with missing meals
                                      if (datesWithMissingMeals.length > 0) {
                                        // Format missing meals data as structured information
                                        const missingMealsStructured = datesWithMissingMeals.map(item => ({
                                          date: format(item.date, 'yyyy-MM-dd'),
                                          missingMealTypes: ['breakfast', 'lunch', 'dinner'].filter(
                                            meal => !item.existingMeals.includes(meal)
                                          )
                                        }));
                                        
                                        // Store structured data in session storage as JSON
                                        sessionStorage.setItem('missingMealsData', JSON.stringify(missingMealsStructured));
                                        
                                        // Create start and end dates based on the missing dates
                                        const startDate = datesWithMissingMeals[0].date;
                                        const endDate = datesWithMissingMeals[datesWithMissingMeals.length - 1].date;
                                        
                                        // Set the date range for the generate modal
                                        setDateRangeStart(startDate);
                                        setDateRangeEnd(endDate);
                                        
                                        // Open generate modal - keep notes field empty for user preferences
                                        setShowGenerateModal(true);
                                        setGenerateNotes('');
                                        setGeneratePeriod('custom'); // Ensure we're in custom mode
                                      } else {
                                        // If there are no missing meals (which shouldn't happen since the button is only visible if fillPercentage < 100)
                                        setSuccessMessage('All days already have complete meal plans!');
                                        setTimeout(() => {
                                          setSuccessMessage('');
                                        }, 3000);
                                      }
                                    }}
                                    disabled={isBefore(dateRangeStart, startOfToday())}
                                  >
                                    <FaUtensils className="mr-1 text-[10px]" />
                                    Fill All Days
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            {/* Progress bar */}
                            <div className="w-full bg-pri-dark/50 rounded-full h-1.5 mt-2">
                              <div 
                                className="bg-gradient-to-r from-soft-pink to-mint-green h-1.5 rounded-full"
                                style={{ width: `${fillPercentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Date entries */}
                    {Object.entries(mealsToDisplay)
                      .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
                      .map(([dateKey, dateMeals]) => {
                        const hasMeals = Object.keys(dateMeals).length > 0;
                        const dateObj = new Date(dateKey);
                        
                        return (
                          <motion.div 
                            key={dateKey} 
                            className={`border-b border-white/10 pb-6 last:border-0 ${hasMeals ? '' : 'opacity-80'}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            data-needs-meals={!hasMeals ? "true" : "false"}
                            id={`date-${dateKey}`}
                          >
                            <div className="flex justify-between items-center mb-4">
                              <h3 className="text-lg font-medium flex items-center text-soft-pink">
                                <FaCalendarAlt className="mr-2 text-soft-pink/70" />
                                {format(dateObj, 'EEEE, MMMM d, yyyy')}
                              </h3>
                              
                              {!hasMeals && (
                                <motion.button
                                  whileHover={!needsPreferences ? { scale: 1.05 } : {}}
                                  whileTap={!needsPreferences ? { scale: 0.95 } : {}}
                                  onClick={() => {
                                    setDateRangeStart(dateObj);
                                    setDateRangeEnd(dateObj);
                                    setGeneratePeriod('day');
                                    setShowGenerateModal(true);
                                  }}
                                  disabled={needsPreferences || isBefore(dateObj, startOfToday())}
                                  className={`text-xs ${
                                    needsPreferences
                                      ? "bg-gray-500/50 cursor-not-allowed text-white/50"
                                      : isBefore(dateObj, startOfToday())
                                        ? "bg-gray-500 cursor-not-allowed"
                                        : "bg-soft-pink/20 hover:bg-soft-pink/30"
                                  } px-2 py-1 rounded flex items-center`}
                                >
                                  <FaUtensils className="mr-1 text-[10px]" />
                                  {needsPreferences ? 'Set Preferences First' : 'Generate Meals'}
                                </motion.button>
                              )}
                            </div>
                            
                            {!hasMeals ? (
                              <div className="bg-pri-dark/20 rounded-lg p-4 border border-white/5 text-center">
                                <p className="text-sm text-pri-white/50">No meals planned for this date.</p>
                              </div>
                            ) : (
                              <div className="relative">
                                <div className="flex overflow-x-auto whitespace-nowrap pb-4 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-3 md:gap-6 md:whitespace-normal">
                                  {['breakfast', 'lunch', 'dinner'].map(mealType => (
                                    <div key={`${dateKey}-${mealType}`} className="inline-block w-[250px] h-[280px] mx-2 whitespace-normal align-top first:ml-4 last:mr-4 md:w-auto md:h-auto md:mx-0 md:first:ml-0 md:last:mr-0 md:block">
                                      <MealCard
                                        meal={dateMeals[mealType]}
                                        mealType={mealType}
                                        onViewDetails={handleViewMealDetails}
                                        onRegenerate={handleRegenerateMeal}
                                        canGenerateMeal={false} // Disable individual generate buttons
                                        date={dateKey}
                                      />
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Indicators for horizontal scroll */}
                                <div className="md:hidden">
                                  <div className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-pri-dark/80 to-transparent w-10 h-full pointer-events-none z-10"></div>
                                  <div className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-l from-pri-dark/80 to-transparent w-10 h-full pointer-events-none z-10"></div>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                  </div>
                )
              )}
            </div>
          )}
          
          {viewMode === 'preferences' && (
            <div>
              <PreferenceForm onComplete={handlePreferencesComplete} />
            </div>
          )}
          
          {viewMode === 'shopping' && (
            <div>

              <ShoppingList />
            </div>
          )}
        </div>
      </div>
      
      {/* Meal Generate Modal */}
      <AnimatePresence>
        {showGenerateModal && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 md:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              // Close modal when clicking the backdrop
              if (e.target === e.currentTarget) {
                handleCancelGenerate();
              }
            }}
          >
            <motion.div
              className="bg-gradient-to-br from-pri-dark to-sec-dark rounded-2xl shadow-2xl w-full max-w-md border border-white/10 overflow-hidden"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with gradient background */}
              <div className="relative bg-gradient-to-r from-soft-pink/30 to-mint-green/30 px-6 py-5 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text flex items-center">
                    <FaUtensils className="text-soft-pink mr-2" />
                    Generate Meal Plan
                  </h3>
                  <motion.button 
                    onClick={handleCancelGenerate}
                    className="text-white/80 hover:text-white bg-black/30 hover:bg-black/50 p-2 rounded-full transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </motion.button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                {/* Generation Period Selection */}
                <div className="mb-5">
                  <label className="block text-white/90 mb-3 font-medium text-sm">Choose a Time Period:</label>
                  
                  {/* Preset Periods */}
                  <div className="flex gap-3 mb-4">
                    <button
                      onClick={() => {
                        setGeneratePeriod('day');
                        
                        // Get today's date
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        
                        // Use selectedDate as the date, but ensure it's not in the past
                        let selectedDay = new Date(selectedDate);
                        if (isBefore(selectedDay, today)) {
                          selectedDay = today;
                        }
                        
                        setDateRangeStart(selectedDay);
                        setDateRangeEnd(selectedDay);
                        setDateRangeError('');
                      }}
                      className={`flex-1 py-2 px-3 rounded-lg border border-white/10 text-sm transition-all ${
                        generatePeriod === 'day'
                          ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg'
                          : 'bg-pri-dark/40 hover:bg-pri-dark/60 text-white/90'
                      }`}
                    >
                      Single Day
                    </button>
                    <button
                      onClick={() => {
                        setGeneratePeriod('week');
                        
                        // Get today's date
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        
                        // Use selectedDate as start date, but ensure it's not in the past
                        let startDate = new Date(selectedDate);
                        if (isBefore(startDate, today)) {
                          startDate = today;
                        }
                        
                        // Calculate end date (MAX_RANGE_DAYS - 1 days from start)
                        const endDate = new Date(startDate);
                        endDate.setDate(startDate.getDate() + MAX_RANGE_DAYS - 1);
                        
                        setDateRangeStart(startDate);
                        setDateRangeEnd(endDate);
                        setDateRangeError('');
                      }}
                      className={`flex-1 py-2 px-3 rounded-lg border border-white/10 text-sm transition-all ${
                        generatePeriod === 'week'
                          ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg'
                          : 'bg-pri-dark/40 hover:bg-pri-dark/60 text-white/90'
                      }`}
                    >
                      {MAX_RANGE_DAYS} Days
                    </button>
                    <button
                      onClick={() => {
                        setGeneratePeriod('custom');
                        setShowDateRangePicker(true);
                      }}
                      className={`flex-1 py-2 px-3 rounded-lg border border-white/10 text-sm transition-all ${
                        generatePeriod === 'custom'
                          ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg'
                          : 'bg-pri-dark/40 hover:bg-pri-dark/60 text-white/90'
                      }`}
                    >
                      Custom Range
                    </button>
                  </div>
                  
                  {/* Date Range Display */}
                  <div className="bg-pri-dark/40 rounded-lg border border-white/10 p-4 relative">
                    <div className="flex justify-between mb-3">
                      <div>
                        <p className="text-xs text-white/60">Start Date</p>
                        <p className="font-medium text-white">{format(dateRangeStart, 'MMMM d, yyyy')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">End Date</p>
                        <p className="font-medium text-white">{format(dateRangeEnd, 'MMMM d, yyyy')}</p>
                      </div>
                    </div>
                    <motion.button
                      onClick={() => setShowDateRangePicker(true)}
                      className="w-full py-2 px-3 rounded-lg flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FaRegCalendarAlt className="text-soft-pink" />
                      <span className="text-sm">{generatePeriod === 'custom' ? 'Select Custom Range' : 'Change Dates'}</span>
                    </motion.button>
                    
                    {/* MiniCalendar Popover in Generate Modal */}
                    <AnimatePresence>
                      {showDateRangePicker && (
                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50">
                          <MiniCalendar 
                            onSelectRange={(start, end) => {
                              handleDateRangeSelect(start, end, 'generate');
                            }}
                            onClose={() => setShowDateRangePicker(false)}
                            initialStartDate={dateRangeStart}
                            initialEndDate={dateRangeEnd}
                            preventPastDates={true} // Prevent selecting past dates for generation
                          />
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                  
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-white/60">
                      {generatePeriod === 'day' 
                        ? `Generating for: ${format(dateRangeStart, 'MMMM d, yyyy')}`
                        : generatePeriod === 'week'
                          ? `Generating for week: ${format(dateRangeStart, 'MMMM d')} - ${format(dateRangeEnd, 'MMMM d, yyyy')}`
                          : `Generating for custom range: ${format(dateRangeStart, 'MMMM d')} - ${format(dateRangeEnd, 'MMMM d, yyyy')}`
                      }
                    </p>
                    <span className="text-xs bg-soft-pink/20 px-2 py-0.5 rounded-full text-white">
                      {Math.round((dateRangeEnd - dateRangeStart) / (1000 * 60 * 60 * 24) + 1)} day{Math.round((dateRangeEnd - dateRangeStart) / (1000 * 60 * 60 * 24)) !== 0 ? 's' : ''}
                    </span>
                  </div>
                  
                  {/* Date Range Error Message */}
                  {dateRangeError && (
                    <div className="mt-3 bg-red-900/30 border border-red-500/30 p-2 rounded-lg">
                      <p className="text-sm text-red-300 flex items-center">
                        <FaTimes className="mr-2 text-red-400 flex-shrink-0" />
                        {dateRangeError}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="mb-5">
                  <label className="block text-white/90 mb-3 font-medium text-sm">Meal Preferences:</label>
                  
                  <div className="bg-gradient-to-r from-soft-pink/10 to-mint-green/10 p-4 rounded-lg border border-white/10 mb-4">
                    <p className="text-sm text-white/90 mb-2 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-soft-pink mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Add specific requests for your meal plan:
                    </p>
                    <ul className="text-xs text-white/70 ml-6 space-y-1 list-disc">
                      <li>"I prefer Asian cuisine with lots of vegetables"</li>
                      <li>"Looking for high-protein meals that are quick to prepare"</li>
                      <li>"Please include Mediterranean dishes with fish"</li>
                    </ul>
                  </div>
                  
                  <textarea
                    value={generateNotes}
                    onChange={(e) => setGenerateNotes(e.target.value)}
                    placeholder="Tell us about your preferences for this meal plan"
                    className="w-full p-3 bg-pri-dark/40 border border-white/10 rounded-lg mb-2 text-white placeholder-white/40 focus:border-soft-pink focus:ring-1 focus:ring-soft-pink focus:outline-none"
                    rows={3}
                  />
                </div>
                
                <div className="flex gap-3 justify-end mt-6">
                  <motion.button
                    onClick={handleCancelGenerate}
                    className="px-4 py-2 border border-white/10 rounded-lg text-white/70 hover:text-white hover:bg-pri-dark/40 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    onClick={handleSubmitGenerate}
                    className={`px-4 py-2 rounded-lg flex items-center ${
                      dateRangeError || status === 'loading'
                        ? 'bg-gray-600/50 text-white/50 cursor-not-allowed'
                        : 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg hover:shadow-xl'
                    }`}
                    disabled={status === 'loading' || !!dateRangeError}
                    whileHover={!dateRangeError && status !== 'loading' ? { scale: 1.02 } : {}}
                    whileTap={!dateRangeError && status !== 'loading' ? { scale: 0.98 } : {}}
                  >
                    {status === 'loading' ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <FaUtensils className="mr-2" />
                        <span>Generate Plan</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal for meal details with animations */}
      {showMealDetails && activeMeal && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-80 flex items-start justify-center z-50 p-2 sm:p-4 md:p-6 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => {
            // Close modal when clicking the backdrop (outside the modal content)
            if (e.target === e.currentTarget) {
              setShowMealDetails(false);
            }
          }}
        >
          <motion.div 
            className="bg-gradient-to-br from-pri-dark to-sec-dark rounded-2xl w-full max-w-4xl my-4 overflow-hidden shadow-2xl border border-white/10 flex flex-col relative"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button - Made more prominent for mobile */}
            <motion.button 
              onClick={() => setShowMealDetails(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 z-50 text-white bg-soft-pink/80 hover:bg-soft-pink p-2.5 sm:p-2 rounded-full transition-colors shadow-lg"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </motion.button>

            {/* Recipe Header with Gradient Overlay */}
            <div className="relative h-40 sm:h-48 md:h-56 w-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-pri-dark/60 via-pri-dark/40 to-pri-dark opacity-90"></div>
              <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-pri-dark to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-20 sm:w-28 md:w-40 h-20 sm:h-28 md:h-40 text-white opacity-20">
                    <path d="M11.45 2.467a11.925 11.925 0 0 1 2.788 0c1.215.167 2.078 1.315 1.911 2.531-.166 1.215-1.314 2.078-2.53 1.911a8.95 8.95 0 0 0-2.08 0c-1.216.167-2.364-.696-2.53-1.911-.167-1.216.695-2.364 1.91-2.531ZM15.648 6.576a7.966 7.966 0 0 0-1.345-.649c-.995-.37-2.089.277-2.459 1.272-.37.994.277 2.088 1.272 2.458.267.1.544.175.828.226.89.161 1.983-.394 2.302-1.292.331-.94-.212-1.972-1.066-2.249a8.912 8.912 0 0 0-1.255-.323 9.627 9.627 0 0 0-2.602 0c-.423.063-.83.162-1.224.312-.855.277-1.398 1.309-1.067 2.25.319.897 1.413 1.452 2.302 1.291.284-.05.561-.126.829-.227.994-.37 1.641-1.463 1.271-2.458-.37-.995-1.463-1.642-2.458-1.272-.359.133-.707.3-1.033.487-.819.47-1.086 1.585-.667 2.427.446.896 1.577 1.258 2.427.668.26-.149.507-.32.741-.513.704-.58.802-1.65.222-2.354-.58-.705-1.649-.803-2.354-.222-.139.114-.271.237-.396.367-.624.644-.604 1.684.041 2.308.645.624 1.685.604 2.308-.04.062-.064.122-.13.18-.2.58-.705.481-1.774-.223-2.354-.705-.58-1.774-.482-2.354.223l-.087.105c-.624.645-.604 1.685.04 2.308.645.624 1.685.604 2.309-.04l.022-.023c.58-.705.48-1.774-.223-2.354-.705-.58-1.775-.482-2.354.222-.58.705-.481 1.775.222 2.354.705.58 1.775.482 2.354-.222.58-.705.482-1.775-.222-2.354-.705-.58-1.775-.482-2.354.222-.58.705-.482 1.775.222 2.354.705.58 1.774.482 2.354-.222.58-.705.482-1.775-.222-2.354Z" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Recipe Title - Improved for mobile screens */}
            <div className="absolute top-14 sm:top-16 md:top-20 left-0 right-0 p-3 sm:p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 sm:gap-3 md:gap-4">
                <div className="max-w-full">
                  <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text break-words">
                    {activeMeal.recipe.name}
                  </h2>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <span className="inline-block px-2 py-0.5 sm:px-3 sm:py-1 bg-gradient-to-r from-soft-pink/20 to-mint-green/20 backdrop-blur-sm rounded-full text-white text-[10px] sm:text-xs font-medium border border-white/10 whitespace-nowrap">
                      {activeMeal.recipe.cuisineType} Cuisine
                    </span>
                    <span className="inline-block px-2 py-0.5 sm:px-3 sm:py-1 bg-gradient-to-r from-soft-pink/20 to-mint-green/20 backdrop-blur-sm rounded-full text-white text-[10px] sm:text-xs font-medium border border-white/10 whitespace-nowrap">
                      {activeMeal.recipe.difficulty} difficulty
                    </span>
                    <span className="inline-block px-2 py-0.5 sm:px-3 sm:py-1 bg-gradient-to-r from-soft-pink/20 to-mint-green/20 backdrop-blur-sm rounded-full text-white text-[10px] sm:text-xs font-medium border border-white/10 whitespace-nowrap">
                      {activeMeal.recipe.prepTime + activeMeal.recipe.cookTime} min total
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3 flex-shrink-0">
                  <motion.button
                    onClick={() => handleRegenerateMeal(activeMeal, activeMeal.notes)}
                    className="px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center text-[10px] sm:text-xs md:text-sm font-medium"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Regenerate Meal
                  </motion.button>
                  
                  <motion.button
                    onClick={() => handlePrintRecipe(activeMeal.recipe)}
                    className="px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg border border-white/20 hover:bg-white/15 transition-all flex items-center text-[10px] sm:text-xs md:text-sm font-medium"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" />
                    </svg>
                    Print Recipe
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Recipe Content - Scrollable with improved mobile support */}
            <div className="flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent" style={{ 
              maxHeight: "calc(100vh - 10rem)",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch"
            }}>
              <div className="p-4 sm:p-6 pb-10">
                {/* Description */}
                <motion.div 
                  className="mb-6 bg-gradient-to-r from-soft-pink/10 to-mint-green/10 p-4 rounded-xl border border-white/5"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <p className="text-pri-white text-base sm:text-lg">{activeMeal.recipe.description}</p>
                </motion.div>
                
                {/* Nutritional Info and Cooking Time in Cards */}
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  {/* Nutritional Information Card */}
                  <div className="bg-gradient-to-br from-soft-pink/10 to-transparent p-4 sm:p-5 rounded-xl border border-white/10">
                    <h3 className="text-base sm:text-lg font-semibold text-soft-pink mb-3 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-14a3 3 0 00-3 3v2H7a1 1 0 000 2h1v1a1 1 0 01-1 1 1 1 0 100 2h6a1 1 0 100-2h-2a1 1 0 00-1-1v-1h1a1 1 0 100-2h-1V7a1 1 0 112 0 1 1 0 102 0 3 3 0 00-3-3z" clipRule="evenodd" />
                      </svg>
                      Nutritional Information
                    </h3>
                    
                    {/* Serving Size Information */}
                    {activeMeal.servingSize && (
                      <div className="mb-3 flex items-center justify-center bg-pri-dark/40 p-2 sm:p-3 rounded-lg">
                        <div className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-soft-pink mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                          </svg>
                          <span className="text-white text-sm sm:text-base font-medium">Recipe serves</span>
                        </div>
                        <div className="ml-auto">
                          <span className="text-base sm:text-lg font-bold text-soft-pink">{activeMeal.servingSize}</span>
                          <span className="text-white ml-1 text-sm sm:text-base">{parseInt(activeMeal.servingSize) === 1 ? 'person' : 'people'}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      <div className="flex items-center bg-pri-dark/40 p-2 sm:p-3 rounded-lg">
                        <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-soft-pink/30 to-transparent flex items-center justify-center">
                          <span className="text-sm sm:text-lg font-bold text-soft-pink">{activeMeal.recipe.nutrients.calories}</span>
                        </div>
                        <div className="ml-2 sm:ml-3">
                          <span className="text-white text-xs sm:text-sm font-medium">Calories</span>
                          <p className="text-xs text-white/60 hidden sm:block">per serving</p>
                        </div>
                      </div>
                      <div className="flex items-center bg-pri-dark/40 p-2 sm:p-3 rounded-lg">
                        <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-soft-pink/30 to-transparent flex items-center justify-center">
                          <span className="text-sm sm:text-lg font-bold text-soft-pink">{activeMeal.recipe.nutrients.protein}g</span>
                        </div>
                        <div className="ml-2 sm:ml-3">
                          <span className="text-white text-xs sm:text-sm font-medium">Protein</span>
                          <p className="text-xs text-white/60 hidden sm:block">per serving</p>
                        </div>
                      </div>
                      <div className="flex items-center bg-pri-dark/40 p-2 sm:p-3 rounded-lg">
                        <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-soft-pink/30 to-transparent flex items-center justify-center">
                          <span className="text-sm sm:text-lg font-bold text-soft-pink">{activeMeal.recipe.nutrients.carbs}g</span>
                        </div>
                        <div className="ml-2 sm:ml-3">
                          <span className="text-white text-xs sm:text-sm font-medium">Carbs</span>
                          <p className="text-xs text-white/60 hidden sm:block">per serving</p>
                        </div>
                      </div>
                      <div className="flex items-center bg-pri-dark/40 p-2 sm:p-3 rounded-lg">
                        <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-soft-pink/30 to-transparent flex items-center justify-center">
                          <span className="text-sm sm:text-lg font-bold text-soft-pink">{activeMeal.recipe.nutrients.fat}g</span>
                        </div>
                        <div className="ml-2 sm:ml-3">
                          <span className="text-white text-xs sm:text-sm font-medium">Fat</span>
                          <p className="text-xs text-white/60 hidden sm:block">per serving</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Time Information Card */}
                  <div className="bg-gradient-to-br from-mint-green/10 to-transparent p-4 sm:p-5 rounded-xl border border-white/10">
                    <h3 className="text-base sm:text-lg font-semibold text-mint-green mb-3 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      Cooking Time
                    </h3>
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex items-center justify-between bg-pri-dark/40 p-3 sm:p-4 rounded-lg">
                        <div className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-mint-green mr-2 sm:mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span className="text-white text-sm sm:text-base">Prep Time</span>
                        </div>
                        <span className="text-base sm:text-lg font-bold text-mint-green">{activeMeal.recipe.prepTime} min</span>
                      </div>
                      <div className="flex items-center justify-between bg-pri-dark/40 p-3 sm:p-4 rounded-lg">
                        <div className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-mint-green mr-2 sm:mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span className="text-white text-sm sm:text-base">Cook Time</span>
                        </div>
                        <span className="text-base sm:text-lg font-bold text-mint-green">{activeMeal.recipe.cookTime} min</span>
                      </div>
                      <div className="flex items-center justify-between bg-gradient-to-r from-mint-green/20 to-transparent p-3 sm:p-4 rounded-lg">
                        <div className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-mint-green mr-2 sm:mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-white text-sm sm:text-base font-medium">Total Time</span>
                        </div>
                        <span className="text-base sm:text-xl font-bold text-mint-green">{activeMeal.recipe.prepTime + activeMeal.recipe.cookTime} min</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                {/* Ingredients */}
                <motion.div 
                  className="mb-6 sm:mb-8"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="text-lg sm:text-xl font-semibold text-soft-pink mb-3 sm:mb-4 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                    Ingredients
                  </h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {activeMeal.recipe.ingredients.map((ingredient, index) => (
                      <motion.li 
                        key={index} 
                        className="flex items-center bg-gradient-to-r from-pri-dark/60 to-pri-dark/40 p-2 rounded"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + (index * 0.03) }}
                      >
                        <span className="w-5 h-5 flex items-center justify-center bg-gradient-to-br from-soft-pink to-mint-green rounded-full mr-2 text-pri-dark font-bold text-xs">
                          {index + 1}
                        </span>
                        <span className="text-pri-white text-sm sm:text-base">
                          {ingredient.quantity} {ingredient.unit} {ingredient.name}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
                
                {/* Cooking Instructions */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <h3 className="text-lg sm:text-xl font-semibold text-mint-green mb-3 sm:mb-4 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                    </svg>
                    Instructions
                  </h3>
                  <ol className="space-y-2 sm:space-y-3">
                    {activeMeal.recipe.instructions.map((step, index) => (
                      <motion.li 
                        key={index} 
                        className="flex bg-pri-dark/30 p-3 rounded"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + (index * 0.05) }}
                      >
                        <span className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 flex items-center justify-center bg-soft-pink rounded-full mr-2 sm:mr-3 text-pri-dark font-bold text-xs sm:text-sm">
                          {index + 1}
                        </span>
                        <span className="text-pri-white text-sm sm:text-base">{step}</span>
                      </motion.li>
                    ))}
                  </ol>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
} 